import React, { ReactNode } from 'react';
import { createReducer, createThunkMiddleware } from '../../hooks';
import { createActions, createRootActions, RootStoreActions, StoreActions } from './actions';
import { reducer } from './reducer';
import { ActionTypes, RootStore, Store } from './types';

type Dispatch = (action: ActionTypes) => void;

const StoreContext = React.createContext<RootStore>({});
const StoreDispatchContext = React.createContext<Dispatch>(() => {});

export const useStore = <T,>(key: string, initialValue?: T): [Store<T>, StoreActions<T>] => {
  const ctx = React.useContext(StoreContext);
  if (!ctx) {
    throw new Error('useStore must be used within StoreContext provider');
  }

  if (!ctx[key]) {
    ctx[key] = {
      key,
      loading: false,
      data: initialValue,
    };
  }

  const dispatch = React.useContext(StoreDispatchContext);

  return [ctx[key] as Store<T>, createActions<T>(key, dispatch, initialValue)];
};

export const useRootStore = <T,>(): [RootStore<T>, RootStoreActions<T>] => {
  const ctx = React.useContext(StoreContext);
  if (!ctx) {
    throw new Error('useStore must be used within StoreContext provider');
  }

  const dispatch = React.useContext(StoreDispatchContext);
  return [ctx as RootStore<T>, createRootActions<T>(dispatch)];
};

export interface StoreProviderProps {
  children: ReactNode;
  initialState: RootStore;
}

export const StoreProvider: React.FC<StoreProviderProps> = ({
  children,
  initialState,
}: StoreProviderProps): React.ReactElement => {
  const thunkMiddleware = createThunkMiddleware<ActionTypes, RootStore>();
  const useReducer = createReducer<ActionTypes, RootStore>(thunkMiddleware);

  const [state, dispatch] = useReducer(reducer, initialState);

  return (
    <StoreContext.Provider value={state}>
      <StoreDispatchContext.Provider value={dispatch}>{children}</StoreDispatchContext.Provider>
    </StoreContext.Provider>
  );
};
